import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  SparklesIcon,
  BoltIcon,
  RocketLaunchIcon,
  ChatBubbleBottomCenterTextIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { ScrollAnimation } from '../components/ScrollAnimation';

// Animations
const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const PageWrapper = styled.div`
  padding: var(--spacing-section-mobile) 0;
  background: linear-gradient(180deg, 
    var(--color-primary-light) 0%, 
    var(--color-primary-base) 50%,
    var(--color-primary-light) 100%);
  min-height: 100vh;
  position: relative;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: var(--spacing-section) 0;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 20% 80%, rgba(11,152,206,0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(49,110,168,0.04) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: var(--spacing-4xl);
  position: relative;
  z-index: 2;
`;

const Title = styled.h1`
  font-size: var(--fs-5xl);
  background: linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-secondary));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: var(--spacing-lg);
  line-height: 1.2;
  
  @media (min-width: 768px) {
    font-size: var(--fs-6xl);
  }
`;

const Subtitle = styled.p`
  font-size: var(--fs-xl);
  color: var(--text-secondary);
  max-width: 800px;
  margin: 0 auto var(--spacing-xl);
  line-height: var(--lh-relaxed);
`;

const ServicesBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-md);
  background: rgba(11,152,206,0.1);
  border: 1px solid rgba(11,152,206,0.2);
  border-radius: 999px;
  color: var(--color-accent-primary);
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--spacing-xl);
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-2xl);
  position: relative;
  z-index: 2;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ServiceCard = styled(Link)`
  display: block;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: var(--spacing-2xl);
  border: 1px solid rgba(11,152,206,0.1);
  text-decoration: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out;

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, 
      var(--color-accent-primary), 
      var(--color-accent-secondary),
      var(--color-accent-primary));
    background-size: 200% 200%;
    animation: ${gradientMove} 8s ease infinite;
    border-radius: 24px;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }

  &:hover {
    transform: translateY(-12px) scale(1.02);
    box-shadow: 0 25px 80px rgba(11,152,206,0.2);
    
    &::before {
      opacity: 0.3;
    }
    
    .service-icon {
      animation: ${float} 2s ease-in-out infinite;
      transform: scale(1.1);
    }
    
    .learn-more {
      svg {
        transform: translateX(6px);
      }
    }
  }
`;

const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-secondary));
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--spacing-lg);
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(11,152,206,0.3);
  
  svg {
    width: 40px;
    height: 40px;
    color: white;
  }
`;

const ServiceTitle = styled.h3`
  font-size: var(--fs-2xl);
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
  font-weight: var(--font-weight-bold);
`;

const ServiceDescription = styled.p`
  color: var(--text-secondary);
  line-height: var(--lh-relaxed);
  margin-bottom: var(--spacing-lg);
  font-size: var(--fs-md);
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 var(--spacing-xl) 0;
  
  li {
    display: flex;
    align-items: center;
    margin-bottom: var(--spacing-sm);
    color: var(--text-secondary);
    font-size: var(--fs-sm);
    
    &:before {
      content: '✓';
      color: var(--color-accent-primary);
      font-weight: var(--font-weight-bold);
      font-size: var(--fs-md);
      margin-right: var(--spacing-sm);
      width: 16px;
      display: flex;
      justify-content: center;
    }
  }
`;

const LearnMoreButton = styled.div`
  color: var(--color-accent-primary);
  font-weight: var(--font-weight-bold);
  font-size: var(--fs-md);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.3s ease;
  }
`;

const StatsSection = styled.section`
  text-align: center;
  margin-top: var(--spacing-4xl);
  padding: var(--spacing-3xl) var(--spacing-xl);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(11,152,206,0.1);
  box-shadow: 0 20px 60px rgba(0,0,0,0.08);
  position: relative;
  z-index: 2;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-xl);
  margin-top: var(--spacing-xl);
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatItem = styled.div`
  .value {
    font-size: var(--fs-3xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-accent-primary);
    display: block;
    line-height: 1;
    margin-bottom: var(--spacing-xs);
  }
  
  .label {
    font-size: var(--fs-sm);
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: var(--ls-wider);
  }
`;

const services = [
  {
    id: 'ai-solutions',
    title: 'AI Solutions',
    description: 'Turn repetitive tasks into superpowers. We implement AI that augments your team\'s capabilities, making them 10x more effective at what they already love doing.',
    features: [
      'Transform mundane workflows into superhero processes',
      'Deploy AI copilots that make employees extraordinarily capable',
      'Automate repetitive work while amplifying human creativity',
      'We eat our own dog food—every solution tested internally first'
    ],
    icon: <SparklesIcon />,
    path: '/services/ai-solutions'
  },
  {
    id: 'it-projects',
    title: 'IT Infrastructure Projects',
    description: 'Storage upgrades, network redeployments, server migrations—complex IT infrastructure projects delivered on time and on budget.',
    features: [
      'Server upgrades and migrations with zero downtime',
      'Network infrastructure redesigns and implementations',
      'Storage system upgrades and data center relocations',
      'Fixed pricing with guaranteed delivery timelines'
    ],
    icon: <RocketLaunchIcon />,
    path: '/services/it-projects'
  },
  {
    id: 'managed-it',
    title: 'Superhero-Level Managed IT',
    description: 'AI-augmented monitoring and management that makes your IT operations superhuman. We use the same AI tools to manage our own infrastructure.',
    features: [
      'AI predicts and prevents issues before they impact workflows',
      'Automated reporting and maintenance that runs itself',
      'Superhuman response times powered by intelligent monitoring',
      'We eat our own dog food—managing our systems with the same AI'
    ],
    icon: <BoltIcon />,
    path: '/services/managed-it'
  },
  {
    id: 'helpdesk',
    title: 'AI-Augmented Support Heroes',
    description: 'Our support team uses AI superpowers to resolve issues faster and smarter than humanly possible.',
    features: [
      'AI-powered diagnostics predict issues before you report them',
      'Instant solutions across all channels with superhuman accuracy',
      'Our support heroes use the same AI tools we implement for you',
      'Turn every support interaction into a superhero experience'
    ],
    icon: <ChatBubbleBottomCenterTextIcon />,
    path: '/services/helpdesk'
  }
];

const Services: React.FC = () => {
  return (
    <PageWrapper>
      <div className="container">
        <HeroSection>
          <ScrollAnimation animation="fadeIn" delay={100}>
            <ServicesBadge>
              <SparklesIcon />
              AI That Actually Works
            </ServicesBadge>
            <Title>Turn Your Team Into AI-Powered Superheroes</Title>
            <Subtitle>
              We don't replace people—we give them superpowers. Every AI solution we build for you, 
              we test ourselves first.
            </Subtitle>
          </ScrollAnimation>
        </HeroSection>

        <ServicesGrid>
          {services.map((service, index) => (
            <ScrollAnimation key={service.id} animation="fadeIn" delay={200 + index * 100}>
              <ServiceCard to={service.path}>
                <ServiceIcon className="service-icon">
                  {service.icon}
                </ServiceIcon>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
                <ServiceFeatures>
                  {service.features.map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ServiceFeatures>
                <LearnMoreButton className="learn-more">
                  Learn More
                  <ArrowRightIcon />
                </LearnMoreButton>
              </ServiceCard>
            </ScrollAnimation>
          ))}
        </ServicesGrid>

        <ScrollAnimation animation="scaleIn" delay={100}>
          <StatsSection>
            <h2>Why Calgary Teams Become Our Workflow Superheroes</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '0' }}>
              We eat our own dog food—these are the same results we achieve with our own AI-augmented operations
            </p>
            <StatsGrid>
              <StatItem>
                <span className="value">25+</span>
                <span className="label">Teams Transformed</span>
              </StatItem>
              <StatItem>
                <span className="value">99.9%</span>
                <span className="label">AI Uptime</span>
              </StatItem>
              <StatItem>
                <span className="value">&lt;15min</span>
                <span className="label">Superhero Response</span>
              </StatItem>
              <StatItem>
                <span className="value">4.8/5</span>
                <span className="label">Superhero Rating</span>
              </StatItem>
            </StatsGrid>
          </StatsSection>
        </ScrollAnimation>
      </div>
    </PageWrapper>
  );
};

export default Services;